"use client";

/**
 * PresenceDot — design language v2
 *
 * Punto de presencia/estado reutilizable. Diseñado para componerse junto a
 * `Avatar` v2 en listas, chat y headers de conversación.
 *
 * Reglas:
 *  · Sin `className`/`style` desde el consumidor (§2.1 design_language_v2.md).
 *  · Variantes mapeadas a los tokens del sistema (§3.1): emerald · gold · slate.
 *  · El borde blanco/zinc del anillo es opt-in via `ringed` para superponerse
 *    sobre avatares oscuros sin desaparecer.
 */

import * as React from "react";
import { cn } from "@/lib/utils";

export type PresenceStatus = "online" | "away" | "offline";
export type PresenceDotSize = "xs" | "sm" | "md";

const SIZE: Record<PresenceDotSize, string> = {
  xs: "h-2 w-2",
  sm: "h-2.5 w-2.5",
  md: "h-3 w-3",
};

const RING: Record<PresenceDotSize, string> = {
  xs: "ring-2",
  sm: "ring-2",
  md: "ring-[2.5px]",
};

const STATUS_COLOR: Record<PresenceStatus, string> = {
  online: "bg-emerald-500",
  away: "bg-amber-400",
  offline: "bg-slate-400 dark:bg-zinc-500",
};

const STATUS_GLOW: Record<PresenceStatus, string> = {
  online: "shadow-[0_0_0_3px_rgba(16,185,129,0.18)]",
  away: "shadow-[0_0_0_3px_rgba(251,191,36,0.18)]",
  offline: "",
};

export interface PresenceDotProps {
  status: PresenceStatus;
  size?: PresenceDotSize;
  /** Anillo blanco/zinc para superposición sobre avatares (default false). */
  ringed?: boolean;
  /** Glow suave del color del estado (solo online/away). */
  glow?: boolean;
  /** Pulso de presencia activa (solo online). */
  pulse?: boolean;
  "aria-label"?: string;
  className?: never;
}

export function PresenceDot({
  status,
  size = "sm",
  ringed = false,
  glow = false,
  pulse = false,
  "aria-label": ariaLabel,
}: PresenceDotProps) {
  return (
    <span
      role="status"
      aria-label={ariaLabel}
      className={cn(
        "inline-block rounded-full",
        SIZE[size],
        STATUS_COLOR[status],
        ringed && cn(RING[size], "ring-white dark:ring-zinc-900"),
        glow && STATUS_GLOW[status],
        pulse && status === "online" && "animate-pulse"
      )}
    />
  );
}

export default PresenceDot;
